import React, { useState } from 'react';
import { createStyles, Header, Container, Group, Burger, Paper, Transition, rem, TextInput, ActionIcon, Drawer, Stack, Button, Indicator } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSelector } from 'react-redux';
import { IconSearch, IconShoppingCart } from '@tabler/icons-react'; // Need to install tabler icons or use existing generic icons if available
import { useNavigate, Link } from 'react-router-dom';

const HEADER_HEIGHT = rem(60);

const useStyles = createStyles((theme) => ({
    root: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        backgroundColor: 'rgba(0, 0, 0, 0.3)', // Dark glass
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'all 0.3s ease',
    },

    dropdown: {
        position: 'absolute',
        top: HEADER_HEIGHT,
        left: 0,
        right: 0,
        zIndex: 0,
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        borderTopWidth: 0,
        overflow: 'hidden',
        backgroundColor: 'rgba(0, 0, 0, 0.9)', // Opaque for dropdown
        backdropFilter: 'blur(12px)',

        [theme.fn.largerThan('sm')]: {
            display: 'none',
        },
    },

    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%',
        maxWidth: '1200px', // Limit width for better aesthetic on large screens
    },

    links: {
        [theme.fn.smallerThan('sm')]: {
            display: 'none',
        },
    },

    burger: {
        [theme.fn.largerThan('sm')]: {
            display: 'none',
        },
    },

    link: {
        display: 'block',
        lineHeight: 1,
        padding: `${rem(8)} ${rem(12)}`,
        borderRadius: theme.radius.xl, // Pill shape
        textDecoration: 'none',
        color: theme.colors.gray[3], // Lighter text for dark bg
        fontSize: theme.fontSizes.sm,
        fontWeight: 500,
        transition: 'all 0.2s ease',

        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: theme.white,
            transform: 'translateY(-1px)',
        },
    },

    searchInput: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: theme.white,
        '&:focus': {
            borderColor: theme.colors.cyan[4],
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.4)',
        }
    }
}));

const Navbar = () => {
    const { classes, theme } = useStyles();
    const [opened, { toggle, close }] = useDisclosure(false);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const cartItems = useSelector((state) => state.cart.cartItems);
    const cartCount = cartItems ? cartItems.reduce((acc, item) => acc + item.quantity, 0) : 0;

    const links = [
        { link: '/', label: 'Home' },
        { link: '/store', label: 'Store' },
        { link: '/upload-and-print', label: 'Upload' },
        { link: '/enquiryform', label: 'Enquiry' },
        // { link: 'https://cdp3d.shiprocket.co/', label: 'Track', external: true },
        { link: '/about', label: 'About' },
        { link: '/contact-us', label: 'Contact' },
    ];

    const items = links.map((link) => {
        if (link.external) {
            return (
                <a
                    key={link.label}
                    href={link.link}
                    className={classes.link}
                    target="_blank"
                    rel="noreferrer"
                >
                    {link.label}
                </a>
            );
        }
        return (
            <Link
                key={link.label}
                to={link.link}
                className={classes.link}
                onClick={close}
            >
                {link.label}
            </Link>
        );
    });

    const handleSearch = (e) => {
        e.preventDefault();
        navigate(`/search/${search}`);
        close();
    }

    return (
        <Header height={HEADER_HEIGHT} className={classes.root}>
            <Container className={classes.header}>
                <Link to="/" style={{ textDecoration: 'none', color: 'white', fontWeight: 'bold', fontSize: '1.4rem', display: 'flex', alignItems: 'center', letterSpacing: '1px' }}>
                    <img src="/logo.png" alt="Logo" width={32} height={32} style={{ marginRight: 12 }} />
                    CDP3D
                </Link>

                <Group spacing={8} className={classes.links}>
                    {items}
                </Group>

                <Group spacing="sm">
                    <Group spacing={8}>
                        {/* Desktop Search Form */}
                        <form onSubmit={handleSearch} className={classes.links}>
                            <TextInput
                                placeholder="Search..."
                                radius="xl"
                                size="sm"
                                value={search}
                                onChange={(event) => setSearch(event.currentTarget.value)}
                                classNames={{ input: classes.searchInput }}
                                rightSection={
                                    <ActionIcon type="submit" size="sm" radius="xl" color="cyan" variant="transparent">
                                        <IconSearch size="1rem" />
                                    </ActionIcon>
                                }
                                rightSectionWidth={36}
                            />
                        </form>

                        {/* Mobile Search Icon */}
                        <ActionIcon
                            size="lg"
                            radius="xl"
                            variant="filled"
                            color="dark"
                            sx={(theme) => ({
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                                [theme.fn.largerThan('sm')]: { display: 'none' }
                            })}
                            onClick={toggle} // Open menu to search
                        >
                            <IconSearch size="1.2rem" />
                        </ActionIcon>

                        <ActionIcon component={Link} to="/cart" size="lg" radius="xl" variant="filled" color="dark" sx={{ backgroundColor: 'rgba(255,255,255,0.05)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                            <Indicator label={cartCount} showZero={false} size={16} color="red" offset={4} inline withBorder processing>
                                <IconShoppingCart size="1.2rem" />
                            </Indicator>
                        </ActionIcon>
                    </Group>

                    <Burger opened={opened} onClick={toggle} className={classes.burger} size="sm" color={theme.white} />
                </Group>
                
                <Drawer
                    opened={opened}
                    onClose={close}
                    size="100%"
                    padding="md"
                    title="Menu"
                    zIndex={1000000}
                    styles={{
                        content: {
                            backgroundColor: 'rgba(5, 5, 5, 0.95)',
                            backdropFilter: 'blur(16px)',
                        },
                        header: {
                            backgroundColor: 'transparent',
                            color: theme.white,
                        },
                        title: {
                            color: theme.white,
                            fontWeight: 700,
                            fontSize: rem(24),
                        },
                        close: {
                            color: theme.white,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                        }
                    }}
                >
                    <Stack spacing="xl" mt="xl" align="center">
                        <form onSubmit={handleSearch} style={{ width: '100%' }}>
                            <TextInput
                                placeholder="Search..."
                                radius="xl"
                                size="md"
                                value={search}
                                onChange={(event) => setSearch(event.currentTarget.value)}
                                classNames={{ input: classes.searchInput }}
                                rightSection={
                                    <ActionIcon type="submit" size="md" radius="xl" color="cyan" variant="transparent">
                                        <IconSearch size="1.2rem" />
                                    </ActionIcon>
                                }
                                rightSectionWidth={42}
                                styles={{ input: { width: '100%', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' } }}
                            />
                        </form>
                        {links.map((link) => (
                            <Link
                                key={link.label}
                                to={link.link}
                                className={classes.link}
                                onClick={close}
                                style={{
                                    fontSize: rem(24),
                                    padding: `${rem(12)} ${rem(24)}`,
                                    width: '100%',
                                    textAlign: 'center',
                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                }}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </Stack>
                </Drawer>
            </Container>
        </Header>
    );
};

export default Navbar;